/**
 * @fileoverview Mock backend for the effective-capabilities endpoint.
 * @description Registers GET /authorization/me/capabilities on the feature's shared mock adapter
 * (see auth-mock-adapter.ts), answering for the user the bearer token belongs to with one PLATFORM
 * grant per permission code their role holds. The codes per role copy the backend's
 * SYSTEM_ROLE_PERMISSION_CATALOGUE, so mock mode runs the same capability-to-privilege translation
 * (capability-access.utils.ts) a real sign-in does. Answers in a single page (nextCursor null). A
 * no-op when VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { authMockAdapter } from './auth-mock-adapter'
import { buildMockErrorEnvelope, buildMockSuccessEnvelope } from './mock-envelope.utils'
import { extractUserIdFromMockToken, getBearerTokenFromConfig } from './mock-token.utils'
import { findMockUserById } from './mock-user-directory'

import type {
  CapabilitiesPage,
  CapabilityGrant,
} from '@/features/auth/services/capabilities.service'

const SALES_MANAGER_CODES = [
  'TENANT.CREATE',
  'TENANT.READ',
  'USER.READ',
  'USER.INVITE',
  'DASHBOARD.READ',
  'REPORT.READ',
  'SALES_CRM.TARGET.ASSIGN',
]

// What a Sales Head holds on top of a Manager's: editing, dropping and reviewing prospects.
const SALES_HEAD_CODES = [
  ...SALES_MANAGER_CODES,
  'REPORT.EXPORT',
  'SALES_CRM.PROSPECT.UPDATE',
  'SALES_CRM.PROSPECT.ARCHIVE',
  'SALES_CRM.PROSPECT.VIEW_PENDING',
  'APPROVAL.DECIDE',
]

const ALL_CODES = [
  'TENANT.CREATE',
  'TENANT.READ',
  'TENANT.UPDATE',
  'USER.READ',
  'USER.INVITE',
  'USER.UPDATE',
  'USER.DEACTIVATE',
  'SESSION.READ',
  'SESSION.REVOKE',
  'DEVICE.READ',
  'DEVICE.REVOKE',
  'AUTHORIZATION.ROLE.READ',
  'AUTHORIZATION.ROLE.CREATE',
  'AUTHORIZATION.ROLE.UPDATE',
  'AUTHORIZATION.ROLE.DEACTIVATE',
  'AUTHORIZATION.PERMISSION.READ',
  'AUTHORIZATION.PERMISSION.GRANT',
  'AUTHORIZATION.PERMISSION.REVOKE',
  'AUTHORIZATION.ASSIGNMENT.READ',
  'AUTHORIZATION.ASSIGNMENT.CREATE',
  'AUTHORIZATION.ASSIGNMENT.REVOKE',
  'AUTHORIZATION.SCOPE.READ',
  'AUTHORIZATION.SCOPE.UPDATE',
  'AUTHORIZATION.RULE.READ',
  'AUTHORIZATION.RULE.UPDATE',
  'NOTIFICATION.CONFIG.READ',
  'NOTIFICATION.CONFIG.UPDATE',
  'NOTIFICATION.TEMPLATE.READ',
  'NOTIFICATION.TEMPLATE.UPDATE',
  'AUDIT.READ',
  'AUDIT.EXPORT',
  'AUDIT.CROSS_TENANT.READ',
  'AUDIT.SENSITIVE_DATA.READ',
  'AUDIT.ARCHIVE.RETRIEVE',
  'DASHBOARD.READ',
  'REPORT.READ',
  'REPORT.EXPORT',
  'DATA_INGESTION.READ',
  'DATA_INGESTION.UPLOAD',
  'SALES_CRM.ORGANISATION_DETAILS.UPDATE',
  'SALES_CRM.TARGET.ASSIGN',
  'SALES_CRM.PROSPECT.UPDATE',
  'SALES_CRM.PROSPECT.ARCHIVE',
  'SALES_CRM.PROSPECT.RESTORE',
  'SALES_CRM.PROSPECT.VIEW_PENDING',
  'APPROVAL.DECIDE',
]

/** Permission codes per mock role key, mirroring the backend's system role catalogue. */
const ROLE_PERMISSION_CODES: Record<string, readonly string[]> = {
  'super-admin': ALL_CODES,
  'sales-head': SALES_HEAD_CODES,
  'sales-manager': SALES_MANAGER_CODES,
  'sales-executive': ['TENANT.CREATE', 'TENANT.READ', 'DASHBOARD.READ', 'REPORT.READ'],
  'bank-hr-head': [
    'USER.READ',
    'USER.INVITE',
    'USER.UPDATE',
    'USER.DEACTIVATE',
    'SESSION.READ',
    'SESSION.REVOKE',
    'DEVICE.READ',
    'DEVICE.REVOKE',
    'AUTHORIZATION.ROLE.READ',
    'AUTHORIZATION.ASSIGNMENT.READ',
    'AUTHORIZATION.ASSIGNMENT.CREATE',
    'AUTHORIZATION.ASSIGNMENT.REVOKE',
    'AUTHORIZATION.SCOPE.READ',
    'AUTHORIZATION.SCOPE.UPDATE',
    'DASHBOARD.READ',
    'REPORT.READ',
  ],
  'bank-tech-admin': ['DATA_INGESTION.READ', 'DATA_INGESTION.UPLOAD'],
  'bank-board-director': ['DASHBOARD.READ', 'REPORT.READ', 'REPORT.EXPORT', 'AUDIT.READ'],
  'bank-regional-manager': ['USER.READ', 'DASHBOARD.READ', 'REPORT.READ', 'REPORT.EXPORT'],
  'bank-branch-manager': ['USER.READ', 'DASHBOARD.READ', 'REPORT.READ'],
}

/**
 * @description Builds one PLATFORM grant for a permission code.
 * @param {string} userId - The grant holder.
 * @param {string} roleKey - The holder's mock role key, standing in for a roleId.
 * @param {string} permissionCode - The permission granted.
 * @returns {CapabilityGrant} The grant.
 */
function buildGrant(userId: string, roleKey: string, permissionCode: string): CapabilityGrant {
  return {
    permissionCode,
    roleId: `mock-role.${roleKey}`,
    assignmentId: `mock-assignment.${userId}.${roleKey}`,
    scopeId: `mock-scope.${userId}.${roleKey}`,
    scope: { scopeType: 'PLATFORM', tenantId: null, regionId: null, branchId: null },
    requiresStepUpMfa: false,
    validFrom: null,
    validUntil: null,
  }
}

if (authMockAdapter) {
  authMockAdapter.onGet('/authorization/me/capabilities').reply((config) => {
    const token = getBearerTokenFromConfig(config)
    const userId = token ? extractUserIdFromMockToken(token) : null
    const user = userId ? findMockUserById(userId) : undefined

    if (!user) {
      return [
        401,
        buildMockErrorEnvelope(401, '1020', 'Authenticated session is invalid or expired.'),
      ]
    }

    const codes = ROLE_PERMISSION_CODES[user.activeRoleKey] ?? []
    const response: CapabilitiesPage = {
      userId: user.id,
      evaluatedAt: new Date().toISOString(),
      grants: codes.map((code) => buildGrant(user.id, user.activeRoleKey, code)),
      nextCursor: null,
    }

    return [200, buildMockSuccessEnvelope(response, 'Capabilities retrieved successfully')]
  })
}
